import { IconButton, Stack, Typography } from '@mui/material'
import { OnboardingStep } from 'api/model/onboardingStep'
import useCheckMobileScreen from 'hooks/use-check-mobile-screen'
import { useActiveOrganization } from 'hooks/use-organization'
import { useEffect, useState } from 'react'
import { useFtuxTooltip } from 'hooks/use-ftux'
import { useFloatingButtonBarVisibility } from 'hooks/use-floating-button-bar-visibility'
import SupportAgentIcon from '@mui/icons-material/SupportAgent'
import CloseIcon from '@mui/icons-material/Close'
import Chatbot from 'components/Chatbot'
import { COLORS } from 'app-constants'

export const checklistOnboardingSteps = [
  OnboardingStep.email_responder,
  OnboardingStep.set_twin_profile,
  OnboardingStep.start_conversation,
  OnboardingStep.twin_share_url_generated,
]

const MyTwinBot = ({ isOrganizationBarEnabled }: { isOrganizationBarEnabled: boolean }) => {
  const [activeOrganization] = useActiveOrganization()

  const isMobile = useCheckMobileScreen()

  const { isFtuxTooltipOpen } = useFtuxTooltip()
  const { isFloatingButtonBarOpen } = useFloatingButtonBarVisibility()

  const isMyTwinBotOpenLocalStorage: boolean | undefined = localStorage.getItem('jelled_isMyTwinBotOpen')
    ? JSON.parse(localStorage.getItem('jelled_isMyTwinBotOpen') || '')
    : undefined
  const [isMyTwinBotOpen, setIsMyTwinBotOpen] = useState(!!isMyTwinBotOpenLocalStorage)

  const [isMyTwinBadgeVisible, setIsMyTwinBadgeVisible] = useState(false)

  useEffect(() => {
    if (activeOrganization && isMyTwinBotOpenLocalStorage === undefined) {
      setIsMyTwinBadgeVisible(true)
    } else {
      setIsMyTwinBadgeVisible(false)
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeOrganization, isFtuxTooltipOpen, isMyTwinBotOpenLocalStorage])

  const getChecklistBottom = () => {
    if (isOrganizationBarEnabled) {
      return isMobile ? '98px' : '118px'
    } else {
      return isMobile ? '98px' : '134px'
    }
  }

  // on mobile we do not show the my twin bot while the ftux tooltips are open
  if ((isMobile && isFtuxTooltipOpen) || window.location.pathname.includes('onboarding')) {
    return null
  }

  return (
    <>
      {isMyTwinBotOpen ? (
        <>
          <IconButton
            onClick={() => {
              setIsMyTwinBotOpen(false)
              localStorage.setItem('jelled_isMyTwinBotOpen', false.toString())
            }}
            sx={{
              backgroundColor: COLORS.JELLED_LIGHT_BLUE,
              position: 'fixed',
              height: isMobile ? '64px' : '80px',
              width: isMobile ? '64px' : '80px',
              bottom: isMobile ? '16px' : '32px',
              right: isMobile ? '16px' : '32px',
              zIndex: 1,
              '&:hover': {
                backgroundColor: COLORS.JELLED_PRIMARY_BLUE,
              },
            }}>
            <CloseIcon
              sx={{
                fill: COLORS.PURE_WHITE,
              }}
            />
          </IconButton>
          <Stack
            zIndex={1} // this is used to make sure the floating checklist is above tooltips but underneath modals
            position="fixed"
            sx={{
              right: isMobile ? '16px' : '32px',
              bottom: getChecklistBottom(),
              height: 'fit-content',
              filter: 'drop-shadow(0px 0px 16px #c7c7c7)',
            }}>
            <Chatbot />
          </Stack>
        </>
      ) : (
        <IconButton
          onClick={() => {
            setIsMyTwinBotOpen(true)
            localStorage.setItem('jelled_isMyTwinBotOpen', true.toString())
          }}
          sx={{
            zIndex: 1,
            position: 'fixed',
            bottom: isMobile ? '16px' : '32px',
            right: isMobile ? '16px' : '32px',
            height: isMobile ? '64px' : '80px',
            width: isMobile ? '64px' : '80px',
            borderRadius: '50%',
            backgroundColor: COLORS.JELLED_LIGHT_BLUE,
            // we hide the button when the floating button bar is open
            display: isFloatingButtonBarOpen ? 'none' : 'flex',
            '&:hover': {
              backgroundColor: COLORS.JELLED_PRIMARY_BLUE,
            },
          }}>
          {isMyTwinBadgeVisible && (
            <Stack
              position="absolute"
              right="92px"
              p="12px 16px"
              justifyContent="center"
              alignItems="center"
              width="230px"
              sx={{
                background: COLORS.PURE_WHITE,
                borderRadius: '40px',
                boxShadow: '2px 2px 8px 0px rgba(0, 0, 0, 0.08)',
                pointerEvents: 'none',
              }}>
              <Typography
                variant="pS"
                sx={{
                  color: COLORS.GRAY_1,
                }}>
                Hi! How can I help you today?
              </Typography>
            </Stack>
          )}
          {isMyTwinBadgeVisible && (
            <Stack
              height="24px"
              width="24px"
              justifyContent="center"
              alignItems="center"
              sx={{
                background: COLORS.RED,
                borderRadius: '50%',
                position: 'absolute',
                left: '50px',
                top: '-8px',
              }}>
              <Typography
                sx={{
                  color: COLORS.PURE_WHITE,
                }}>
                1
              </Typography>
            </Stack>
          )}
          <SupportAgentIcon
            sx={{
              height: '40px',
              width: '36px',
              fill: COLORS.PURE_WHITE,
            }}
          />
        </IconButton>
      )}
    </>
  )
}

export default MyTwinBot
